import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import api from '../utils/axios';
import { toast } from 'sonner';
import {
  Landmark, Download, Loader2, FileSpreadsheet, Scale, TrendingUp,
  Users, ArrowLeftRight, CheckCircle2, AlertTriangle, RefreshCw, History,
  XCircle, ArrowUp, ArrowDown, Info, MessageSquare,
} from 'lucide-react';

const fmtInt = (n) => new Intl.NumberFormat('es-AR').format(Math.round(Number(n) || 0));
const fmtMoney = (n) => '$ ' + new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const KPI = ({ label, value, icon: Icon, color = '#0f766e', sub }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="bcra-kpi">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{label}</p>
        <p className="mt-1 text-xl font-extrabold tabular-nums" style={{ color }}>{value}</p>
        {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
      </div>
      {Icon && <div className="rounded-lg p-2" style={{ backgroundColor: `${color}15` }}><Icon size={18} style={{ color }} /></div>}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, children, hint }) => (
  <div className="flex items-center gap-2 mb-2">
    <Icon size={15} className="text-teal-600" />
    <h3 className="text-sm font-bold text-slate-800">{children}</h3>
    {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
  </div>
);

export default function BcraReport() {
  const [periodos, setPeriodos] = useState([]);
  const [periodo, setPeriodo] = useState('');
  const [loading, setLoading] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [data, setData] = useState(null);
  const [verif, setVerif] = useState(null);
  const [verifLoading, setVerifLoading] = useState(false);
  const [presentaciones, setPresentaciones] = useState([]);
  const [puedeSaldos, setPuedeSaldos] = useState(false);
  const [tiiPrefijo, setTiiPrefijo] = useState('');

  useEffect(() => { loadPeriodos(); loadPresentaciones(); cargarPermisoSaldos(); cargarTiiPrefijo(); }, []);

  const cargarTiiPrefijo = async () => {
    try {
      const r = await api.get('/bcra-report/tii-config');
      setTiiPrefijo(r.data?.prefijo_recaudador || '');
    } catch (e) { /* noop */ }
  };

  const guardarTiiPrefijo = async () => {
    try {
      await api.put('/bcra-report/tii-config', { prefijo_recaudador: tiiPrefijo });
      toast.success('Prefijo del CBU recaudador guardado');
      if (periodo && data) generar();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar el prefijo'); }
  };

  const cargarPermisoSaldos = async () => {
    try {
      const r = await api.get('/permisos-v2/puede-recuadro', { params: { key: 'bcra-saldos-historicos' } });
      setPuedeSaldos(!!r.data?.puede);
    } catch (e) { setPuedeSaldos(false); }
  };

  const loadPeriodos = async () => {
    try {
      const r = await api.get('/bcra-report/periodos');
      const ps = r.data.periodos || [];
      setPeriodos(ps);
      if (ps.length) setPeriodo((prev) => prev || ps[0].value);
    } catch (e) { toast.error('No se pudieron cargar los períodos (¿MySQL?)'); }
  };

  const loadPresentaciones = async () => {
    try { const r = await api.get('/bcra-report/presentaciones'); setPresentaciones(r.data.presentaciones || []); } catch {}
  };

  const generar = async () => {
    if (!periodo) { toast.warning('Elegí un período'); return; }
    setLoading(true); setData(null);
    try {
      const r = await api.post('/bcra-report/generar', { periodo });
      setData(r.data);
      toast.success(`Reporte ${r.data.mes_label} ${periodo.slice(0, 4)} generado`);
      loadPresentaciones();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el reporte');
    } finally { setLoading(false); }
  };

  const descargar = async () => {
    if (!periodo) return;
    setDescargando(true);
    try {
      const r = await api.get(`/bcra-report/export/${periodo}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `BCRA_${data?.mes_label || periodo}_${periodo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      loadPresentaciones();
    } catch (e) { toast.error('Error al descargar el Excel'); }
    finally { setDescargando(false); }
  };

  const cargarVerif = async () => {
    setVerifLoading(true);
    try { const r = await api.get('/bcra-report/verificacion/saldos'); setVerif(r.data); }
    catch (e) { toast.error('Error al calcular la verificación de saldos'); }
    finally { setVerifLoading(false); }
  };

  const cargarGuardada = async (p) => {
    setPeriodo(p);
    setLoading(true); setData(null);
    try {
      const r = await api.get(`/bcra-report/${p}`);
      setData(r.data);
    } catch (e) {
      toast.info('Presentación no encontrada, generá el reporte.');
    } finally { setLoading(false); }
  };

  const res = data?.resumen || {};

  return (
    <div className="px-6 pb-6 pt-20 space-y-5" data-testid="bcra-report">
      {/* Header + acciones */}
      <div className="bcra-header p-5 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg p-2" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}><Landmark size={22} /></div>
            <div>
              <h2 className="text-lg font-bold">Reporte BCRA — Régimen Informativo PSP</h2>
              <p className="text-xs opacity-90">Padrón (altas/bajas) · AP A (saldo diario) · AP B (transferencias) · Conteos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-teal-50 hidden sm:inline">Período</span>
            <select
              data-testid="bcra-periodo-select"
              value={periodo}
              onChange={(e) => { setPeriodo(e.target.value); setData(null); }}
              className="h-9 rounded-md border-2 border-white bg-white px-3 text-sm font-semibold text-slate-800 shadow-sm cursor-pointer">
              {periodos.length === 0 && <option value="">Sin períodos</option>}
              {periodos.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <Button onClick={generar} disabled={loading || !periodo} data-testid="bcra-generar-btn"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold">
              {loading ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Generando…</> : <><RefreshCw size={15} className="mr-1.5" /> Generar</>}
            </Button>
            <Button onClick={descargar} disabled={descargando || !periodo} data-testid="bcra-export-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {descargando ? <><Loader2 size={15} className="animate-spin mr-1.5" /> …</> : <><Download size={15} className="mr-1.5" /> Excel</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {loading && (
        <Card className="p-16 text-center border-dashed border-2" data-testid="bcra-loading">
          <Loader2 size={40} className="mx-auto text-teal-500 animate-spin mb-3" />
          <p className="text-slate-500 text-sm">Consultando MySQL y armando el reporte…</p>
        </Card>
      )}

      {!loading && !data && (
        <Card className="p-14 text-center border-dashed border-2" data-testid="bcra-empty">
          <FileSpreadsheet size={44} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">Elegí un período y tocá <strong>Generar</strong> para ver el reporte, o <strong>Excel</strong> para descargarlo.</p>
        </Card>
      )}

      {data && (
        <div className="space-y-5" data-testid="bcra-preview">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Altas del mes" value={fmtInt(res.altas)} icon={Users} color="#0f766e" sub="estado 10" />
            <KPI label="Bajas del mes" value={fmtInt(res.bajas)} icon={AlertTriangle} color="#dc2626" sub="estado 20" />
            <KPI label="Transferencias (cant.)" value={fmtInt(res.ap_b_cant_total)} icon={ArrowLeftRight} color="#2563eb" sub={fmtMoney(res.ap_b_monto_total)} />
            <KPI label="Saldo final del mes" value={fmtMoney(res.ap_a_saldo_final)} icon={Scale} color="#7c3aed" sub={`Base: ${fmtMoney(res.ap_a_saldo_base)}`} />
          </div>

          {res.ap_a_saldo_base_origen === 'movimientos_acumulado' && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="bcra-base-nota">
              <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">No hay <strong>bcra_info</strong> del mes anterior, así que la <strong>base del saldo</strong> se calculó sumando los movimientos acumulados desde el inicio (saldo 0). Cargá el cierre del mes anterior en <code>bcra_info</code> para usar el saldo oficial.</p>
            </div>
          )}

          {/* AP B - transferencias */}
          <Card className="p-4" data-testid="bcra-apb">
            <SectionTitle icon={ArrowLeftRight} hint="AP B · códigos 500xxx">Transferencias del mes</SectionTitle>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b">
                  <th className="p-2">Código</th><th className="p-2">Concepto</th><th className="p-2 text-right">Cantidad</th><th className="p-2 text-right">Monto total</th>
                </tr></thead>
                <tbody>
                  {(data.ap_b || []).map((r) => (
                    <tr key={r.codigo} className="border-b border-slate-100" data-testid={`bcra-apb-row-${r.codigo}`}>
                      <td className="p-2 font-mono text-xs text-slate-500">{r.codigo}</td>
                      <td className="p-2 text-slate-700">{r.concepto}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{fmtInt(r.cantidad)}</td>
                      <td className="p-2 text-right tabular-nums text-slate-700">{fmtMoney(r.monto_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* AP A - saldo diario */}
            <Card className="p-4" data-testid="bcra-apa">
              <SectionTitle icon={TrendingUp} hint="AP A · saldo diario">Saldo diario</SectionTitle>
              <div className="overflow-y-auto max-h-80 rounded border border-slate-100">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
                    <th className="p-2">Fecha</th><th className="p-2 text-right">Mov. día</th><th className="p-2 text-right">Saldo</th><th className="p-2 text-right">Usu. únicos</th>
                  </tr></thead>
                  <tbody>
                    {(data.ap_a?.filas || []).map((r) => (
                      <tr key={r.fecha} className="border-b border-slate-50">
                        <td className="p-2 text-slate-500">{r.fecha}</td>
                        <td className={`p-2 text-right tabular-nums ${r.mov_del_dia < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{fmtInt(r.mov_del_dia)}</td>
                        <td className="p-2 text-right tabular-nums font-semibold text-slate-700">{fmtInt(r.saldo_final)}</td>
                        <td className="p-2 text-right tabular-nums text-slate-500">{fmtInt(r.usuarios_acumulados_unicos)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Conteos 600xxx */}
            <Card className="p-4" data-testid="bcra-conteos">
              <SectionTitle icon={Users} hint="códigos 600xxx">Conteos de clientes y cuentas</SectionTitle>
              <div className="overflow-y-auto max-h-80 rounded border border-slate-100">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
                    <th className="p-2">Partida</th><th className="p-2">Concepto</th><th className="p-2 text-right">Cant.</th>
                  </tr></thead>
                  <tbody>
                    {(data.conteos_600 || []).map((r) => (
                      <tr key={r.codigo_partida} className="border-b border-slate-50">
                        <td className="p-2 font-mono text-[10px] text-slate-500">{r.codigo_partida}</td>
                        <td className="p-2 text-slate-600 leading-tight">{r.concepto}</td>
                        <td className="p-2 text-right tabular-nums font-semibold">{fmtInt(r.cantidad)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Altas y bajas */}
          <Card className="p-4" data-testid="bcra-altas-bajas">
            <SectionTitle icon={Users} hint={`hoja "ALTAS Y BAJAS" · ${(data.altas_bajas || []).length} registros`}>Altas y bajas del padrón</SectionTitle>
            <div className="overflow-y-auto max-h-96 rounded border border-slate-100">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
                  <th className="p-2">CUIT/CUIL</th><th className="p-2">Nombre/Razón social</th><th className="p-2">CP</th><th className="p-2">Estado</th>
                </tr></thead>
                <tbody>
                  {(data.altas_bajas || []).map((r, i) => (
                    <tr key={`${r.cuit}-${i}`} className="border-b border-slate-50">
                      <td className="p-2 font-mono text-slate-600">{r.cuit}</td>
                      <td className="p-2 text-slate-700">{r.nombre}</td>
                      <td className="p-2 text-slate-400">{r.codigo_postal}</td>
                      <td className="p-2">
                        <Badge className={r.estado === '10' ? 'bg-teal-100 text-teal-700' : 'bg-red-100 text-red-700'}>
                          {r.estado === '10' ? 'Alta (10)' : 'Baja (20)'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* R.I. Transferencias inmediatas intraentidades (T.I.I. · A 8427) */}
          {data.tii && (
          <Card className="p-4" data-testid="bcra-tii">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
              <SectionTitle icon={ArrowLeftRight} hint='Com. "A" 8427 · modelo CVU (PSPCP)'>Transferencias inmediatas intraentidades (R.I. – T.I.I.)</SectionTitle>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-400">CBU recaudador (prefijo)</span>
                <Input value={tiiPrefijo} onChange={(e) => setTiiPrefijo(e.target.value)} className="h-8 w-40 font-mono text-xs" data-testid="bcra-tii-prefijo" />
                <Button size="sm" variant="outline" onClick={guardarTiiPrefijo} className="text-xs" data-testid="bcra-tii-guardar">Guardar y recalcular</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b">
                  <th className="p-2">Cód. Partida</th><th className="p-2">Concepto</th><th className="p-2">Tipo</th><th className="p-2">Administrador</th><th className="p-2">Moneda</th><th className="p-2 text-right">Cantidad</th><th className="p-2 text-right">Monto (miles)</th>
                </tr></thead>
                <tbody>
                  {(data.tii.partidas || []).map((p) => (
                    <tr key={p.codigo_partida} className="border-b border-slate-100" data-testid={`bcra-tii-row-${p.codigo_partida}`}>
                      <td className="p-2 font-mono text-xs text-slate-500">{p.codigo_partida}</td>
                      <td className="p-2 text-slate-700">{p.concepto}</td>
                      <td className="p-2 text-xs text-slate-500">{p.tipo_transferencia} · {p.tipo_transferencia_desc}</td>
                      <td className="p-2 text-xs text-slate-500">{p.participacion_admin} · {p.participacion_admin_desc}</td>
                      <td className="p-2 text-xs">{p.moneda}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{fmtInt(p.cantidad)}</td>
                      <td className="p-2 text-right tabular-nums text-slate-700">{fmtInt(p.monto_miles)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Importes en <strong>miles de ARS</strong> (sin decimales). Transferencias internas CVU→CVU cuyo origen y destino pertenecen al mismo CBU recaudador (prefijo configurable), deduplicadas por operación. Por ahora se informa la partida <strong>2010100</strong>.</p>
          </Card>
          )}

          {/* RECLAMOS (R.I. Atención al usuario) */}
          {data.reclamos && (
          <Card className="p-4" data-testid="bcra-reclamos">
            <SectionTitle icon={MessageSquare} hint="R.I. Atención al usuario · hoja RECLAMOS">Reclamos del mes</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="bcra-reclamos-ingresados">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Casos ingresados</p>
                <p className="mt-1 text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(data.reclamos.casos_ingresados)}</p>
                <p className="text-[11px] text-slate-400">Tickets creados en el mes</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid="bcra-reclamos-resueltos">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Cerrados / Resueltos</p>
                <p className="mt-1 text-xl font-extrabold tabular-nums text-emerald-700">{fmtInt(data.reclamos.casos_resueltos)}</p>
                <p className="text-[11px] text-slate-400">Estado Cerrado o Resuelto</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="bcra-reclamos-afavor">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Resueltos a favor</p>
                <p className="mt-1 text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(data.reclamos.a_favor)}</p>
                <p className="text-[11px] text-slate-400">En contra: {fmtInt(data.reclamos.en_contra)}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="bcra-reclamos-plazo">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Plazo promedio</p>
                <p className="mt-1 text-xl font-extrabold tabular-nums text-slate-800">{fmtInt(data.reclamos.plazo_promedio)}</p>
                <p className="text-[11px] text-slate-400">días (fijo)</p>
              </div>
            </div>
            <div className="overflow-x-auto rounded border border-slate-100">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase text-slate-400 border-b bg-slate-50">
                  <th className="p-2">Periodo</th><th className="p-2 text-right">Ingresados</th><th className="p-2 text-right">Resueltos</th><th className="p-2 text-right">Plazo prom.</th><th className="p-2 text-right">A favor</th><th className="p-2 text-right">En contra</th><th className="p-2">Conceptos</th>
                </tr></thead>
                <tbody>
                  <tr className="border-b border-slate-100" data-testid="bcra-reclamos-row">
                    <td className="p-2 font-mono text-xs text-slate-500">{data.reclamos.periodo}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{fmtInt(data.reclamos.casos_ingresados)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtInt(data.reclamos.casos_resueltos)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtInt(data.reclamos.plazo_promedio)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtInt(data.reclamos.a_favor)}</td>
                    <td className="p-2 text-right tabular-nums">{fmtInt(data.reclamos.en_contra)}</td>
                    <td className="p-2 text-xs text-slate-600">{data.reclamos.conceptos}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {!data.reclamos.tiene_datos && (
              <p className="mt-2 text-[11px] text-amber-600">No hay tickets guardados para este período. Corré la automatización de Atención al Cliente (Freshdesk) del mes para poblar los datos.</p>
            )}
            <p className="mt-2 text-[11px] text-slate-400">Fuente: tickets de Atención al Cliente guardados. <strong>Plazo promedio</strong>, <strong>resueltos a favor/en contra</strong> y <strong>conceptos</strong> son valores fijos por ahora (a definir con el criterio regulatorio final).</p>
          </Card>
          )}
        </div>
      )}
      {puedeSaldos && (
      <Card className="p-4" data-testid="bcra-verificacion">
        <div className="flex items-center justify-between mb-2">
          <SectionTitle icon={Scale} hint="movimientos vs bcra_info">Verificación de saldos históricos</SectionTitle>
          <Button variant="outline" size="sm" onClick={cargarVerif} disabled={verifLoading} data-testid="bcra-verif-btn" className="text-xs">
            {verifLoading ? <><Loader2 size={13} className="animate-spin mr-1" /> Calculando…</> : <><RefreshCw size={13} className="mr-1" /> Calcular</>}
          </Button>
        </div>
        {!verif ? (
          <p className="text-xs text-slate-400">Barrida acumulada desde el primer movimiento (saldo inicial = 0) comparada contra lo informado a BCRA, para detectar correcciones hacia atrás.</p>
        ) : (
          <div className="space-y-3">
            {/* Detector de desfases */}
            {verif.alertas && verif.alertas.length > 0 ? (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3" data-testid="bcra-alertas">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="text-amber-600" size={16} />
                  <p className="text-sm font-bold text-amber-900">
                    Detector de desfases: {verif.alertas.length} {verif.alertas.length === 1 ? 'mes requiere' : 'meses requieren'} atención
                    {verif.resumen?.alertas_criticas ? ` · ${verif.resumen.alertas_criticas} crítico(s)` : ''}
                  </p>
                </div>
                <div className="space-y-1.5">
                  {verif.alertas.map((a) => (
                    <div key={a.presentacion} className="flex items-start gap-2 text-xs" data-testid={`bcra-alerta-${a.presentacion}`}>
                      {a.severidad === 'critico'
                        ? <XCircle size={14} className="text-red-500 mt-0.5 shrink-0" />
                        : <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />}
                      <span className="text-slate-700">
                        <strong className={a.severidad === 'critico' ? 'text-red-700' : 'text-amber-800'}>{a.presentacion}</strong>
                        {' '}— desfase de <strong>$ {fmtInt(a.ajuste_mes)}</strong>. {a.motivo}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3" data-testid="bcra-alertas-ok">
                <CheckCircle2 className="text-emerald-600" size={16} />
                <p className="text-sm font-semibold text-emerald-800">Sin desfases relevantes: ningún mes supera el umbral de alerta.</p>
              </div>
            )}

            {/* Resumen */}
            {verif.resumen && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Meses barridos</p>
                  <p className="text-sm font-bold text-slate-700 tabular-nums">{fmtInt(verif.resumen.meses)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Saldo acumulado (movs)</p>
                  <p className="text-sm font-bold text-slate-700 tabular-nums">$ {fmtInt(verif.resumen.saldo_acumulado_final)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Dif. acumulada final</p>
                  <p className="text-sm font-bold tabular-nums text-slate-700">$ {fmtInt(verif.resumen.diferencia_acumulada_final)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Meses con alerta</p>
                  <p className={`text-sm font-bold tabular-nums ${verif.resumen.alertas_criticas ? 'text-red-600' : verif.resumen.alertas_total ? 'text-amber-600' : 'text-emerald-600'}`}>{fmtInt(verif.resumen.alertas_total)}</p>
                </div>
              </div>
            )}

            {/* Leyenda de columnas */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-[11px] text-slate-500 leading-relaxed" data-testid="bcra-leyenda">
              <div className="flex items-center gap-1.5 mb-1 text-slate-600 font-semibold"><Info size={13} /> Cómo leer esta tabla</div>
              <ul className="space-y-0.5">
                <li><strong>Mov. neto mes</strong> = ingresos − egresos del mes (lo que entró menos lo que salió). En rojo si salió más.</li>
                <li><strong>Saldo acum. (movs)</strong> = saldo que deberías tener sumando todos los movimientos desde el inicio (arranca en 0).</li>
                <li><strong>Saldo BCRA</strong> = saldo que se informó a BCRA ese mes.</li>
                <li><strong>Dif. acumulada</strong> = Saldo BCRA − Saldo acum. <span className="text-emerald-600 font-medium">▲ tenés más</span> saldo que lo informado / <span className="text-red-600 font-medium">▼ tenés menos</span>.</li>
                <li><strong>Ajuste del mes</strong> = cuánto cambió esa diferencia vs el mes anterior. Cerca de 0 = mes OK; grande = desfase nuevo a revisar.</li>
              </ul>
              <p className="mt-1 text-slate-400">Primer movimiento: {verif.primer_movimiento || '—'}.</p>
            </div>

            <div className="overflow-x-auto rounded border border-slate-100 max-h-[28rem] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-100"><tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                  <th className="p-2">Período</th>
                  <th className="p-2 text-right" title="Cantidad de movimientos del mes">Movs</th>
                  <th className="p-2 text-right" title="Ingresos menos egresos del mes">Mov. neto mes</th>
                  <th className="p-2 text-right" title="Suma de movimientos netos desde el inicio (saldo 0)">Saldo acum. (movs)</th>
                  <th className="p-2 text-right" title="Saldo informado a BCRA">Saldo BCRA</th>
                  <th className="p-2 text-right" title="Saldo BCRA − Saldo acumulado. Negativo = tenés más saldo que lo informado">Dif. acumulada</th>
                  <th className="p-2 text-right" title="Variación de la diferencia acumulada vs el mes anterior">Ajuste del mes</th>
                  <th className="p-2 text-center">Estado</th>
                </tr></thead>
                <tbody>
                  {(verif.filas || []).map((f) => {
                    const aj = f.ajuste_mes;
                    const sinBcra = f.saldo_bcra === null || f.saldo_bcra === undefined;
                    const sev = f.severidad;
                    const ajColor = sev === 'critico' ? 'text-red-600' : sev === 'alto' ? 'text-amber-600' : sev === 'ok' ? 'text-emerald-600' : 'text-slate-300';
                    const difNull = f.diferencia_acumulada === null || f.diferencia_acumulada === undefined;
                    return (
                      <tr key={f.presentacion} className={`border-b border-slate-50 ${sinBcra ? 'bg-slate-50/40' : ''}`} data-testid={`bcra-verif-row-${f.presentacion}`}>
                        <td className="p-2 font-mono text-slate-600">{f.presentacion}</td>
                        <td className="p-2 text-right tabular-nums text-slate-400">{fmtInt(f.movimientos)}</td>
                        <td className={`p-2 text-right tabular-nums ${f.net_mov_mes < 0 ? 'text-red-500' : 'text-slate-500'}`}>{fmtInt(f.net_mov_mes)}</td>
                        <td className="p-2 text-right tabular-nums font-semibold text-slate-700">{fmtInt(f.saldo_acumulado)}</td>
                        <td className="p-2 text-right tabular-nums text-slate-500">{sinBcra ? '—' : fmtInt(f.saldo_bcra)}</td>
                        <td className="p-2 text-right tabular-nums">
                          {difNull ? <span className="text-slate-300">—</span> : (
                            <span className={`inline-flex items-center gap-1 ${f.direccion === 'mas' ? 'text-emerald-600' : f.direccion === 'menos' ? 'text-red-600' : 'text-slate-500'}`} title={f.direccion === 'mas' ? 'Según movimientos tenés MÁS saldo que lo informado' : f.direccion === 'menos' ? 'Según movimientos tenés MENOS saldo que lo informado' : ''}>
                              {f.direccion === 'mas' ? <ArrowUp size={12} /> : f.direccion === 'menos' ? <ArrowDown size={12} /> : null}
                              {fmtInt(f.diferencia_acumulada)}
                            </span>
                          )}
                        </td>
                        <td className={`p-2 text-right tabular-nums font-semibold ${ajColor}`}>{aj === null || aj === undefined ? '—' : fmtInt(aj)}</td>
                        <td className="p-2 text-center" title={sev === 'critico' ? 'Error probable de carga' : sev === 'alto' ? 'Desfase importante, revisar' : sev === 'ok' ? 'Mes OK' : ''}>
                          {!sev ? <span className="text-slate-300">—</span>
                            : sev === 'ok' ? <CheckCircle2 size={15} className="inline text-emerald-500" />
                            : sev === 'alto' ? <AlertTriangle size={15} className="inline text-amber-500" />
                            : <XCircle size={15} className="inline text-red-500" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
      )}

      {/* Presentaciones guardadas */}
      {presentaciones.length > 0 && (
        <Card className="p-4" data-testid="bcra-presentaciones">
          <SectionTitle icon={History} hint="MongoDB">Presentaciones guardadas</SectionTitle>
          <div className="flex flex-wrap gap-2">
            {presentaciones.map((p) => (
              <button key={p.periodo} data-testid={`bcra-pres-${p.periodo}`}
                onClick={() => cargarGuardada(p.periodo)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs hover:border-teal-400 transition-colors">
                <span className="font-semibold text-slate-700">{p.mes_label} {p.periodo.slice(0, 4)}</span>
                <span className="ml-2 text-slate-400">{p.resumen?.altas ?? 0}↑ / {p.resumen?.bajas ?? 0}↓</span>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
